import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';
import { CartFacade } from '@symphony-solo/domain/cart/src';
import { ChooseLocationFacade, MenuProduct, Price, Timeslot } from '@symphony-solo/domain/choose-location/src';
import { CompanyConfigFacade, CompanyConfigType } from '@symphony-solo/domain/company-config/src';
import { OrderItem, ChildItem, Instruction, OrderFacade } from '@symphony-solo/domain/order/src';
import { AlertService } from '@symphony-solo/shared/alert/src';
// import { LocalStorageService } from '@symphony-solo/shared/local-storage/src';
import { PlatformIdentityService } from '@symphony-solo/shared/platform-identity/src';
import { ToastService } from '@symphony-solo/shared/toast/src/lib/services/toast.service';
import { DateHelper, roundDecimals } from '@symphony-solo/shared/utils/src';
import { Subscription } from 'rxjs';

@Component({
  selector: 'symphony-solo-add-to-cart-modal',
  templateUrl: './add-to-cart-modal.component.html',
  styleUrls: ['./add-to-cart-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddToCartModalComponent implements OnInit, OnDestroy {
  @ViewChild('bannerImg', { static: true }) public bannerImgEl: ElementRef;
  @ViewChildren('accordion') public accordions!: QueryList<ElementRef>;
  public hideAfterScrollHeight;
  public currencySymbol = '£';
  public product = this.navParams.get('product');
  public cartItem = this.navParams.get('cartItem');
  public cartItemIndex = this.navParams.get('cartItemIndex') || -1;
  public extraObj = this.navParams.get('extras');
  public allergens = this.navParams.get('allergens');
  public soloMenuMode = this.navParams.get('soloMenuMode');
  public modifyMode = this.navParams.get('modifyMode');
  public alergensLength = null;
  public productdetail: MenuProduct;
  public quantity = 1;
  public condimentsObj: any;
  public validationCheck: boolean;
  public isListItemOpened: { [key: string]: boolean } = {};
  public isCondimentClicked = false;
  public modifiers = [];
  public selectedModifier: Price;
  private cartProduct: OrderItem = {
    productName: this.product?.name,
    productID: this.product?.productID,
    menuID: this.extraObj?.menuID,
    menuItemID: this.extraObj?.menuItemID,
    productGroup: this.product?.productGroup,
    notes: '',
    quantity: null,
    total: null,
    childItems: [],
    oPrice: null,
    instructions: [],
    periodID: null,
    imageURL: null,
    modifier: null,
    restrictedAge: 0,
  };
  private timeSlot: Timeslot;
  private subscriptions: Subscription[] = [];
  private acceptedAgeRestrictionYears = [];
  private companyConfigIndex = [];

  constructor(
    public cartFacade: CartFacade,
    public orderFacade: OrderFacade,
    public modalCtrl: ModalController,
    public platform: PlatformIdentityService,
    public chooseLocationFacade: ChooseLocationFacade,
    private alertService: AlertService,
    private cdf: ChangeDetectorRef,
    private navParams: NavParams,
    private toastService: ToastService,
    public companyConfigFacade: CompanyConfigFacade
  ) {}

  public ngOnInit(): void {
    this.productdetail = JSON.parse(JSON.stringify(this.product));
    this.modifiers = this.productdetail?.prices
      .filter((price) => price.name !== 'Standard Modifier')
      .map((p) => {
        return { ...p, isDisabled: false, isChecked: false };
      });
    this.selectedModifier = this.getStandardModifier();

    this.productdetail.isValid = true;
    if (this.productdetail?.allergens) {
      this.alergensLength = this.productdetail?.allergens.filter((x) => x !== '');
    }
    if (this.productdetail?.childItems?.length) {
      for (const [index, value] of this.productdetail?.childItems.entries()) {
        if (value.minQty) {
          this.productdetail.isValid = false;
        }
        for (const p_item of value?.childItems) {
          const itemFound = this.cartItem?.childItems.find((p) => p.productID === p_item.productID);

          p_item['quantity'] = 0;

          if (p_item?.prices?.length) {
            p_item['oPrice'] = itemFound ? itemFound.oPrice : p_item?.prices[0].price;
          }

          p_item['isChecked'] = itemFound ? true : false;
          p_item['isDisabled'] = false;
        }
        // Initialize accordion open state for each item to true by default
        this.isListItemOpened[index] = true;
      }
      this.cdf.detectChanges();
    }

    this.isListItemOpened[99] = true; // for modifiers

    if (this.modifyMode) {
      this.modifyProduct();
    }
    this.checkValidationForSubmit();
    this.allSubscriptions();
  }

  public ionViewDidEnter(): void {
    this.hideAfterScrollHeight = this.bannerImgEl?.nativeElement?.clientHeight;
  }

  public toggleAccordion(itemId: string): void {
    this.isListItemOpened[itemId] = !this.isListItemOpened[itemId];
  }

  public getAllergensName(code: string) {
    if (code.length) {
      const allergen = this.allergens.find((x) => x.code === code);
      return allergen?.name;
    } else {
      return null;
    }
  }

  public dismissModal() {
    // using the injected ModalController this page
    // can "dismiss" itself and optionally pass back data
    this.modalCtrl.dismiss({
      dismissed: true,
    });
  }

  public cancel() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  public confirm() {
    // this.navController.navigateRoot([CHOOSE_MENU, {selectedTime: dataObj.time }]);
    return this.modalCtrl.dismiss('dataObj', 'confirm');
  }

  // Check for unselected required condiments
  public scrollToFirstUnselectedCondiment() {
    const childItems = this.productdetail?.childItems || [];

    // Find the first required group where no items are selected
    const firstUnselectedIndex = childItems.findIndex(
      (condiments) => condiments.isRequired === false // Option is required
    );

    if (firstUnselectedIndex !== -1) {
      // Open the accordion if it's closed
      this.isListItemOpened[firstUnselectedIndex] = true;

      // Scroll to the required accordion
      const targetAccordion = this.accordions.toArray()[firstUnselectedIndex];
      targetAccordion.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  public handleAddToCartClick() {
    if (!this.productdetail?.isValid) {
      this.isCondimentClicked = true;
      this.scrollToFirstUnselectedCondiment();
      this.toastService.showErrorToastOnTop("You haven't made all your selections yet");
    } else {
      this.addToCart(this.productdetail);
    }
  }

  public showModifiers() {
    return this.modifiers && this.modifiers.length > 0;
  }

  public onModifierChange(e, modifier) {
    if (e.detail.checked) {
      this.modifiers = this.modifiers.map((price) => {
        return { ...price, isDisabled: price.id !== modifier.id, isChecked: price.id === modifier.id };
      });
      this.selectedModifier = { ...modifier, oPrice: modifier.price };
    } else {
      this.selectedModifier = this.getStandardModifier();
      this.modifiers = this.modifiers.map((p) => {
        return { ...p, isDisabled: false, isChecked: false };
      });
    }
  }

  public addToCart(selectedProduct: MenuProduct) {
    this.orderFacade.clearState('createSession');

    const price = roundDecimals(this.selectedModifier.oPrice * this.quantity, 2);

    if (!selectedProduct?.stopped) {
      this.cartProduct.quantity = this.quantity;
      this.cartProduct.oPrice = this.selectedModifier.price;
      this.cartProduct.total = { ...this.selectedModifier, price: price, oPrice: this.selectedModifier.oPrice };
      this.cartProduct.stopped = selectedProduct?.stopped;
      this.cartProduct.periodID = this.timeSlot.periodID;
      this.cartProduct.imageURL = selectedProduct.imageURL;
      this.cartProduct.modifier = this.selectedModifier;
      this.cartProduct.restrictedAge = selectedProduct.restrictedAge;

      this.cartFacade.addToCart(this.cartProduct, this.cartItemIndex);
      this.orderFacade.addOrderItem(this.cartProduct, this.cartItemIndex);
    }

    this.dismissModal();
  }

  public changeQuantity(type: 'Increament' | 'Decreament') {
    // const p = this.cartProduct;
    switch (type) {
      case 'Increament':
        this.quantity++;
        break;
      case 'Decreament':
        if (this.quantity > 1) {
          this.quantity--;
        }
        break;
      default:
        break;
    }

    this.onEachQtyChange();
    this.cartFacade.calculateSubtotal();
    this.cdf.detectChanges();
  }

  public changeCondiments(item: any, itemParent: any, addmore?: string, action?: string | 'Decreament' | 'Increament') {
    let checkQtyCount = 0;
    const condiments: ChildItem = {
      productName: null,
      productID: null,
      menuID: this.extraObj?.menuID,
      menuItemID: null,
      notes: '',
      quantity: item?.quantity ? item?.quantity : 1,
      total: null,
      oQty: item?.quantity ? item?.quantity : 1,
      oPrice: item?.oPrice,
    };
    const instructions: Instruction = {
      id: null,
      name: null,
      oQty: item?.quantity ? item?.quantity : 1,
      quantity: item?.quantity ? item?.quantity : 1,
      menuID: this.extraObj?.menuID,
      menuItemID: null,
    };

    const c_lengthObj = this.selectedCheckboxes(itemParent);
    if (addmore === 'addmore') {
      // only the first time will come here to expand the + button
      // + indiviual count of cond (selected cond count)
      if (itemParent.maxPerItem !== c_lengthObj) item.isChecked = true;
      if (itemParent.maxPerItem > 1 && itemParent.maxPerItem === checkQtyCount) {
        this.makeItemsDisabled(itemParent);
      } else {
        this.makeItemsEnabled(itemParent);
      }
      item.addmore = true;
      if (item?.itemType !== 'Instruction') {
        condiments.addMore = true;
      } else {
        instructions.addMore = true;
      }
    }

    if (itemParent.maxPerItem > 1 ? itemParent.maxPerItem >= c_lengthObj : itemParent?.maxQty >= c_lengthObj) {
      let found = null;
      if (item.isChecked) {
        if (item?.itemType !== 'Instruction') {
          condiments.productName = item.name;
          condiments.productID = item.productID;
          condiments.total = item?.prices[0];
          condiments.menuID = this.extraObj?.menuID;
          condiments.menuItemID = item.id;
          condiments.productGroup = item.productGroup;
          this.cartProduct?.childItems?.forEach((x, i) => {
            if (x.productID === item.productID) {
              found = {
                item: x,
                index: i,
              };
            }
          });
        } else {
          instructions.name = item.name;
          instructions.id = item.id;
          this.cartProduct?.instructions?.forEach((x, i) => {
            if (x.id === item.id) {
              found = {
                item: x,
                index: i,
              };
            }
          });
        }
        if (action === 'Increament') {
          if (item?.itemType !== 'Instruction') {
            this.selectedModifier.price += +item.prices[0].price.toFixed(2);
          }
          if (!found) {
            item.quantity = item.quantity + 1;
            if (item?.itemType !== 'Instruction') {
              condiments.oQty = item.quantity;
              this.cartProduct.childItems.push(condiments);
            } else {
              instructions.oQty = item.quantity;
              this.cartProduct.instructions.push(instructions);
            }
          } else if (found) {
            item.quantity = item.quantity + 1;
            if (item?.itemType !== 'Instruction') {
              condiments.oQty = +condiments.oQty + 1;
              const foundObj = this.cartProduct.childItems[found.index];
              foundObj.quantity = condiments.oQty;
              foundObj.oQty = condiments.oQty;
              foundObj.total = { ...foundObj.total, price: +(item.oPrice * condiments.oQty).toFixed(2) };
            } else {
              instructions.oQty = +instructions.oQty + 1;

              const foundObj = this.cartProduct.instructions[found.index];
              foundObj.quantity = instructions.oQty;
              foundObj.oQty = instructions.oQty;
            }
          }
          checkQtyCount = this.checkQtyCount(itemParent);
          if (checkQtyCount >= itemParent.maxPerItem) {
            itemParent.isDisabled = true;
            this.makeItemsDisabled(itemParent);
            this.cdf.detectChanges();
          } else {
            itemParent.isDisabled = false;
            this.makeItemsEnabled(itemParent);
          }
        } else if (action === 'Decreament') {
          if (item?.quantity === 1) {
            if (item?.itemType !== 'Instruction') {
              this.selectedModifier.price -= +item.prices[0].price.toFixed(2);
            }
            item.addmore = false;
            item.isChecked = false;
            item.quantity = item.quantity - 1;
            if (item?.itemType !== 'Instruction') {
              condiments.oQty = condiments.oQty - 1;
              this.cartProduct.childItems = this.cartProduct.childItems.filter((itm, index) => {
                if (index !== found.index) {
                  return itm;
                }
              });
            } else {
              instructions.oQty = instructions.oQty - 1;
              this.cartProduct.instructions = this.cartProduct.instructions.filter((itm, index) => {
                if (index !== found.index) {
                  return itm;
                }
              });
            }
          } else {
            if (found) {
              item.quantity = item.quantity - 1;
              if (item?.itemType !== 'Instruction') {
                condiments.oQty = +condiments.oQty - 1;
                this.selectedModifier.price -= +item.prices[0].price.toFixed(2);
                const foundObj = this.cartProduct.childItems[found.index];
                foundObj.quantity = condiments.oQty;
                foundObj.oQty = condiments.oQty;
                foundObj.total = { ...foundObj.total, price: +(item.oPrice * condiments.oQty).toFixed(2) };
              } else {
                instructions.oQty = +instructions.oQty - 1;
                const foundObj = this.cartProduct.instructions[found.index];
                foundObj.quantity = instructions.oQty;
                foundObj.oQty = instructions.oQty;
              }
            }
          }
          this.makeItemsEnabled(itemParent);
          checkQtyCount = this.checkQtyCount(itemParent);
          if (checkQtyCount >= itemParent.maxPerItem) {
            itemParent.isDisabled = true;
            this.makeItemsDisabled(itemParent);
            this.cdf.detectChanges();
          } else {
            itemParent.isDisabled = false;
            this.makeItemsEnabled(itemParent);
          }
        } else {
          if (item?.itemType === 'Instruction') {
            // const instruction = {
            //   id: item?.id,
            //   name: item?.name,
            // };

            this.cartProduct.instructions.push(instructions);
          } else {
            this.selectedModifier.price += +item.prices[0].price.toFixed(2);
            this.cartProduct.childItems.push(condiments);
          }
        }
      } else {
        if (item?.itemType === 'Instruction') {
          this.cartProduct.instructions = this.cartProduct.instructions.filter((i) => i.id !== item.id);
        } else {
          this.selectedModifier.price -= +item.prices[0].price.toFixed(2);
          this.cartProduct.childItems = this.cartProduct.childItems.filter((i) => i.productID !== item.productID);
        }
        this.makeItemsEnabled(itemParent);
      }
      if (itemParent.maxPerItem > 1 ? itemParent.maxPerItem === checkQtyCount : itemParent.maxQty === c_lengthObj) {
        this.makeItemsDisabled(itemParent);
      }
    } else {
      item.isChecked = false;
      item.addmore = false;
      itemParent.childItems.filter((i) => {
        i.isDisabled = false;
      });
    }
    if (condiments?.oPrice) {
      condiments.oPrice = +item.prices[0].price.toFixed(2);
      condiments.total = { ...condiments.total, price: +(item.oPrice * condiments.oQty).toFixed(2) };
    }
    this.onEachQtyChange();
    this.checkValidationForSubmit();
    this.cdf.detectChanges();
  }

  public checkProductAgeRestriction() {
    const ageRestrictionYear = this.productdetail.restrictedAge;
    if (ageRestrictionYear) {
      if (this.acceptedAgeRestrictionYears.includes(ageRestrictionYear)) {
        this.handleAddToCartClick();
      } else {
        const date = this.subtractYears(new Date(), ageRestrictionYear);

        const ageRestrictionMsg = this.companyConfigIndex[CompanyConfigType.SoloRestrictedAgeMessageText];

        const okButton = {
          text: 'Continue',
          cssClass: 'okButton centerButton',
          handler: async () => {
            this.chooseLocationFacade.setAcceptedAgeRestrictionYears(ageRestrictionYear);
            this.handleAddToCartClick();
          },
        };
        const cancelButton = {
          text: 'Cancel',
          cssClass: 'cancelBtn centerButton',
          handler: () => {
            this.dismissModal();
          },
        };
        this.alertService.presentAlert(
          'Age Restriction',
          ageRestrictionMsg.replaceAll('DD MM YYYY', DateHelper.formatDateToDDMMYYYY(date)),
          [okButton, cancelButton],
          false,
          null,
          false
        );
      }
    } else {
      this.handleAddToCartClick();
    }
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private subtractYears(date, years) {
    date.setFullYear(date.getFullYear() - years);
    return date;
  }

  private onEachQtyChange() {
    if (this.quantity >= 1) {
      this.cartProduct.childItems.filter((cond) => {
        if (cond.addMore) {
          cond.quantity = +this.quantity * +cond.oQty;
        } else {
          cond.quantity = +this.quantity;
        }
        if (+cond?.oPrice > 0) {
          cond.total.price = +(cond?.oPrice * +cond.quantity).toFixed(2);
        }
      });
      this.cartProduct.instructions.filter((cond) => {
        if (cond.addMore) {
          cond.quantity = +this.quantity * +cond.oQty;
        } else {
          cond.quantity = +this.quantity;
        }
      });
    }
  }

  private makeItemsDisabled(itemParent: any) {
    itemParent?.childItems.filter((i) => {
      if (i.isChecked === false) {
        i.isDisabled = true;
      }
    });
  }

  private makeItemsEnabled(itemParent: any) {
    itemParent?.childItems.filter((i) => {
      if (i.isChecked === false) {
        i.isDisabled = false;
      }
    });
  }

  private checkQtyCount(itemParent: any) {
    return itemParent.childItems.reduce((total, num) => {
      return total + num.quantity;
    }, 0);
  }

  private checkValidationForSubmit() {
    if (this.productdetail?.childItems?.length) {
      this.productdetail?.childItems?.forEach((i) => {
        if (i.minQty) {
          const minQtyCount = this.selectedCheckboxes(i);
          if (minQtyCount >= i.minQty) {
            i.isRequired = true;
          } else {
            i.isRequired = false;
          }
        }
      });
      for (const i of this.productdetail?.childItems) {
        if (i.minQty) {
          const minQtyCount = this.selectedCheckboxes(i);
          if (minQtyCount >= i.minQty) {
            this.productdetail.isValid = true;
          } else {
            this.productdetail.isValid = false;
            break;
          }
        }
      }
      this.cdf.detectChanges();
    }
  }

  private selectedCheckboxes(itemParent?: any): number {
    let count = 0;
    itemParent?.childItems?.filter((i) => {
      if (i.isChecked === true) {
        count += i?.quantity ? i?.quantity : 1;
      }
    });
    return count;
  }

  private allSubscriptions() {
    this.subscriptions.push(
      this.chooseLocationFacade.selectedTimeslot$.subscribe((timeSlot) => {
        this.timeSlot = timeSlot;
      }),
      this.chooseLocationFacade.acceptedAgeRestrictionYears$.subscribe((acceptedAgeRestrictionYears) => {
        this.acceptedAgeRestrictionYears = acceptedAgeRestrictionYears;
      }),
      this.companyConfigFacade.companyConfigIndex$.subscribe((companyConfigIndex) => {
        this.companyConfigIndex = companyConfigIndex;
      })
    );
  }

  private modifyProduct(): void {
    if (this.cartItem) {
      this.productdetail.childItems?.forEach((course) => {
        course.childItems?.forEach((condiment) => {
          let itemFound: any;
          if (condiment?.itemType !== 'Instruction') {
            itemFound = this.cartItem.childItems.find((p) => p.productID === condiment.productID);
          } else {
            itemFound = this.cartItem.instructions.find((p) => p.id === condiment.id);
          }
          if (itemFound) {
            if (course.maxPerItem < 2) {
              // Ensure the condiment isn't duplicated in childItems or instructions
              if (
                !this.cartItem.childItems.some((p) => p.productID === condiment.productID) &&
                !this.cartItem.instructions.some((p) => p.id === condiment.id)
              ) {
                this.changeCondiments(condiment, course);
              }
            } else {
              for (let n = 1; n <= itemFound.quantity; n++) {
                this.changeCondiments(condiment, course, 'addmore', 'Increament');
              }
            }
          }
        });
      });

      for (let i = 1; i < this.cartItem.quantity; i++) {
        this.changeQuantity('Increament');
      }
    }
  }

  private getStandardModifier() {
    const modifier = this.productdetail?.prices.find((p) => p.name === 'Standard Modifier');
    return { ...modifier, oPrice: modifier.price };
  }
}
